import { Check, TriangleAlert } from 'lucide-react'
import { Badge } from '@/components/ui/badge'

export type VisibilityDiagnostic = {
  appears?: boolean
  message?: string
  conditions: Array<{
    name: string
    passes: boolean
    fix: string | null
  }>
  public_condition?: {
    name: string
    passes: boolean
    fix: string | null
    rendering: string
  }
}

type Condition = {
  id: string
  passLabel: string
  failLabel: string
  passes: boolean
  fix?: string | null
  publicOnly?: boolean
}

const memberRenderingIds = [
  'not-archived',
  'membership-status',
  'level-included',
  'member-not-hidden',
  'association-directory-on',
]

export function VisibilityDiagnosticPanel({
  diagnostic,
  showTitle = true,
}: {
  diagnostic: VisibilityDiagnostic
  showTitle?: boolean
}) {
  if (diagnostic.message) {
    return (
      <div className="rounded-md border border-border bg-card p-4">
        {showTitle && (
          <h2 className="pb-2 text-body font-medium text-foreground">Directory visibility</h2>
        )}
        <p className="text-body-sm text-muted-foreground">{diagnostic.message}</p>
      </div>
    )
  }

  const conditions = buildConditions(diagnostic)
  const listed = memberRenderingIds.every(
    (id) => conditions.find((condition) => condition.id === id)?.passes,
  )

  return (
    <div className="overflow-hidden rounded-md border border-border bg-card">
      {showTitle && (
        <h2 className="px-4 py-3 text-body font-medium text-foreground">Directory visibility</h2>
      )}
      <div className="flex items-center gap-2 border-y border-border px-4 py-3">
        {listed ? <CheckIcon /> : <WarningIcon />}
        <span className="text-body-sm font-medium text-foreground">
          {listed ? 'Listed in the member directory' : 'Not listed in the member directory'}
        </span>
      </div>
      {conditions.map((condition, index) => (
        <div
          key={condition.id}
          className={`flex min-h-9 items-center justify-between gap-4 px-4 py-2 ${index > 0 ? 'border-t border-border' : ''}`}
        >
          <div className="flex min-w-0 items-center gap-2 text-body-sm">
            {condition.passes ? <CheckIcon /> : <WarningIcon />}
            <span className="text-foreground">
              {condition.passes ? condition.passLabel : condition.failLabel}
            </span>
            {condition.publicOnly && <Badge variant="neutral">Public only</Badge>}
          </div>
          {!condition.passes && condition.fix && (
            <a
              href={fixHref(condition.fix)}
              className="shrink-0 text-caption text-muted-foreground hover:text-foreground"
            >
              {fixLabel(condition.fix)}
            </a>
          )}
        </div>
      ))}
    </div>
  )
}

function buildConditions(diagnostic: VisibilityDiagnostic): Condition[] {
  const source = diagnostic.conditions
  const condition = (id: string, passLabel: string, failLabel: string, name: string): Condition => {
    const found = source.find((item) => item.name === name)
    return { id, passLabel, failLabel, passes: found?.passes ?? false, fix: found?.fix }
  }

  const conditions = [
    condition(
      'not-archived',
      'The contact is not archived.',
      'The contact is archived.',
      'not archived',
    ),
    condition(
      'membership-status',
      'The membership is Active or Pending-Renewal.',
      'The membership is not Active or Pending-Renewal.',
      'qualifying membership',
    ),
    condition(
      'level-included',
      'The membership level is included in the directory.',
      'The membership level is excluded from the directory.',
      'included Membership Level',
    ),
    condition(
      'member-not-hidden',
      'The member has not hidden themselves.',
      'The member has hidden themselves. Staff cannot override this choice.',
      'member has not hidden themselves',
    ),
    condition(
      'association-directory-on',
      "The association's directory is switched on.",
      "The association's directory is switched off.",
      'members-only rendering is enabled',
    ),
  ]

  if (diagnostic.public_condition) {
    conditions.push({
      id: 'public-opt-in',
      passLabel: 'The member opted in to public listing.',
      failLabel: 'The member has not opted in to public listing.',
      passes: diagnostic.public_condition.passes,
      fix: diagnostic.public_condition.fix,
      publicOnly: true,
    })
  }

  return conditions
}

function fixLabel(fix: string) {
  return fix.includes('Membership Level') ? 'Membership levels' : 'Directory settings'
}

function fixHref(fix: string) {
  return fix.includes('Membership Level')
    ? '/admin/directory/settings#membership-levels'
    : '/admin/directory/settings#renderings'
}

function CheckIcon() {
  return <Check className="size-4 shrink-0 text-success" strokeWidth={1.5} aria-hidden="true" />
}

function WarningIcon() {
  return (
    <TriangleAlert className="size-4 shrink-0 text-warning" strokeWidth={1.5} aria-hidden="true" />
  )
}
